/**
 * ScrollReveal - 高性能滚动动画控制器
 * 默认类名: `.gsap-reveal`（可自定义）
 * 依赖: GSAP 3.x + ScrollTrigger（需提前全局引入）
 */
class ScrollReveal {
    constructor(options = {}) {
        // 默认配置
        const defaults = {
            trigger: '.gsap-reveal',  // 目标元素选择器
            from: { opacity: 0, y: 40 },  // 初始状态
            to: {
                opacity: 1,
                y: 0,
                duration: 0.5,
                ease: "power2.out",
                stagger: 0.01,          // 元素之间的动画间隔时间
            },
            scrollTrigger: {
                start: "top 80%",      // 触发位置
                toggleActions: "play none none none", // 仅在进入时播放
                markers: false,        // 调试用标记
                once: false
            },
            immediateIfVisible: true,
            batchSize: 50,             // 每批处理的元素数量
            rootMargin: '200px 0px',   // IntersectionObserver 的提前加载区域
            staggerDirection: 'forward' // stagger方向: 'forward' | 'backward' | 'random'
        };

        // 合并用户配置
        this.settings = { ...defaults, ...options };
        this.elements = [];
        this.observer = null;
        this.processedElements = new WeakSet();
        this.batchIndex = 0;
        this.activeAnimations = new WeakMap();

        // 初始化
        this.init();
    }

    init() {
        // 检查 GSAP 和 ScrollTrigger 是否可用
        if (!this.checkDependencies()) return;

        // 注册插件
        gsap.registerPlugin(ScrollTrigger);

        // 获取所有目标元素并过滤掉 display: none 的元素
        this.elements = gsap.utils.toArray(this.settings.trigger).filter(el => {
            return window.getComputedStyle(el).display !== 'none';
        });

        // 如果没有元素，停止执行
        if (!this.elements.length) {
            console.warn(`No visible elements found with selector: "${this.settings.trigger}"`);
            return;
        }

        // 设置初始状态
        gsap.set(this.elements, this.settings.from);

        // 立即显示视口内元素
        if (this.settings.immediateIfVisible) {
            this.animateVisibleImmediately();
        }

        // 初始化 IntersectionObserver
        this.initObserver();
    }

    // 检查依赖
    checkDependencies() {
        if (typeof gsap === 'undefined') {
            console.error('GSAP is not loaded!');
            return false;
        }
        if (typeof ScrollTrigger === 'undefined') {
            console.error('ScrollTrigger is not loaded!');
            return false;
        }
        return true;
    }

    // 初始化 IntersectionObserver
    initObserver() {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !this.processedElements.has(entry.target)) {
                    this.processElement(entry.target);
                    this.processedElements.add(entry.target);
                    
                    // 如果设置为只执行一次，则取消观察
                    if (this.settings.scrollTrigger.once) {
                        this.observer.unobserve(entry.target);
                    }
                }
            });
        }, {
            rootMargin: this.settings.rootMargin,
            threshold: 0.01
        });

        // 初始观察和处理第一批元素
        this.processNextBatch();
    }

    // 处理单个元素动画
    processElement(element) {
        const { to, scrollTrigger } = this.settings;
        
        // 计算当前元素在所有元素中的索引
        const index = this.elements.indexOf(element);
        
        // 计算延迟时间 - 确保动画依次出现
        let delay = index * to.stagger;
        
        // 根据方向调整延迟
        if (this.settings.staggerDirection === 'backward') {
            delay = (this.elements.length - index - 1) * to.stagger;
        } else if (this.settings.staggerDirection === 'random') {
            delay = Math.random() * (this.elements.length * to.stagger);
        }
        
        // 创建动画时间线
        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: element,
                start: scrollTrigger.start,
                toggleActions: scrollTrigger.toggleActions,
                markers: scrollTrigger.markers,
                once: scrollTrigger.once,
                fastScrollEnd: true
            }
        });

        tl.to(element, {
            opacity: to.opacity,
            y: to.y,
            duration: to.duration,
            ease: to.ease,
            delay: delay
        });

        // 存储动画引用以便后续管理
        this.activeAnimations.set(element, tl);
    }

    // 分批处理元素
    processNextBatch() {
        const batchSize = this.settings.batchSize;
        const endIndex = Math.min(this.batchIndex + batchSize,	this.elements.length);
        
        for (let i = this.batchIndex; i < endIndex; i++) {
            this.observer.observe(this.elements[i]);
        }
        
        this.batchIndex = endIndex;
        
        // 如果还有未处理的元素，延迟处理下一批
        if (this.batchIndex < this.elements.length) {
            requestIdleCallback(() => {
                this.processNextBatch();
            });
        }
    }

    // 立即显示视口内元素（优化版）
    animateVisibleImmediately() {
        const { to } = this.settings;
        const viewportHeight = window.innerHeight;
        
        // 使用更高效的可见性检测
        const visibleElements = this.elements.filter(element => {
            const rect = element.getBoundingClientRect();
            return rect.top < viewportHeight && rect.bottom > 0;
        });

        visibleElements.forEach((element, index) => {
            // 直接设置最终状态，避免动画延迟
            gsap.set(element, {
                opacity: to.opacity,
                y: to.y
            });
            
            // 标记为已处理
            this.processedElements.add(element);
        });
    }

    // 刷新方法，重新计算位置
    refresh() {
        ScrollTrigger.refresh();
    }

    // 销毁方法，清理资源
    destroy() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
        
        // 清理所有动画
        this.elements.forEach(element => {
            const animation = this.activeAnimations.get(element);
            if (animation) {
                animation.kill();
            }
        });
        
        this.activeAnimations = new WeakMap();
        this.processedElements = new WeakSet();
        
        // 清理所有 ScrollTrigger 实例
        ScrollTrigger.getAll().forEach(st => st.kill());
    }
}

// 自动初始化（可选）
document.addEventListener('DOMContentLoaded', () => {
    window.ScrollReveal = new ScrollReveal(); // 全局可访问
});
